import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import styles from './list.module.css';
import { Popover } from '@mui/material';
import { ChevronDown, ChevronUp, ChevronRight, Code, Edit, Trash2, MoreVertical, Send } from 'react-feather';
import { useState, Fragment, useEffect, useRef, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import { useAuth0 } from '@auth0/auth0-react';
import { Dropdown } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { TableLoader } from '../../../../../components/LoaderC';
import { buildFilterParams } from '../../../vehicleQuery';
import { canSendUnlockLink, isRejectedRequest, isVehicleRow, mapVehicleRow, rowPhotos, rowThumb } from '../../vehicleRows';
import ListThumb from '../../ListThumb';
import ApprovalBadge from '../../ApprovalBadge';
// import PassCell from '../../PassCell';
import VehicleDetailPanel from '../../VehicleDetailPanel';
import MobileViewList from './MobileViewList';

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;
const COLUMN_COUNT = 7;

const SortIcon = ({ active, order }) => {
  if (!active) return <Code className={`${styles.Normal}`} />;
  return order === "asc" ? <ChevronDown /> : <ChevronUp />;
};

const VehicleList = ({ isActive, query, refreshKey, onListChange, onMeta, actions, setTenantModalOpen, setTenantDetails }) => {
  const redirect = useHistory();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const permissions = (isAuthenticated && user?.["https://propertydek.com/permissions"]) || [];
  const canUpdate = permissions.indexOf("vehicle:update") > -1;
  const [allData, setAllData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const loadedKeyRef = useRef(null);
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [sortItem, setSortItem] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [carImage, setCarImage] = useState("");
  const [tenantImage, setTenantImage] = useState("");

  const carPopoverActionRef = useRef(null);
  const tenantPopoverActionRef = useRef(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCarImage("");
  };

  const handlePopoverOpen2 = (event) => {
    setAnchorEl2(event.currentTarget);
  };
  const handlePopoverClose2 = () => {
    setAnchorEl2(null);
    setTenantImage("");
  };

  const open = Boolean(anchorEl);
  const open2 = Boolean(anchorEl2);

  const toggleRow = (rowKey) => {
    setExpandedRowKey(prev => prev === rowKey ? null : rowKey);
  };

  // Sorting, search and filters run on the server (v1/vehicles/list); rows arrive in chunks
  const fetchVehicles = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { ...buildFilterParams(query), offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
    if (sortItem !== "") {
      params.sort_by = sortItem;
      params.sort_order = sortOrder;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/vehicles/list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};

      setAllData((prev) => (replace ? newChunk : prev.concat(newChunk)));
      setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
      setHasMore(Boolean(meta?.has_more));

      if (onMetaRef.current) onMetaRef.current(meta);

      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Vehicles] Failed to load vehicle list", error);
      toast.error(error?.response?.data?.message || "Unable to load vehicles. Please try again.");

      if (replace) {
        // Allow the next activation of the list view to try again
        loadedKeyRef.current = null;
        setLoadError(true);
        setAllData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, query, sortItem, sortOrder]);

  // Load the first chunk only while the list view is visible, and only if search, filters,
  // sorting or data changed since the last load (switching views back doesn't refetch)
  const requestKey = JSON.stringify([query, sortItem, sortOrder, refreshKey]);
  useEffect(() => {
    if (!isActive || loadedKeyRef.current === requestKey) return;
    loadedKeyRef.current = requestKey;
    fetchVehicles(0, true);
  }, [isActive, requestKey, fetchVehicles]);

  // Infinite scroll: load the next chunk when the sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!isActive || !node) return;

    const observer = new IntersectionObserver((entries) => {
      const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchVehicles(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [isActive, hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchVehicles]);

  const rowsData = allData.map(mapVehicleRow);

  const changeSortOrder = (type) => {
    if (type === sortItem) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortOrder("asc");
    }
    setSortItem(type);
  }

  const goToTenantetails = (row) => {
    if (permissions.indexOf("tenant-contactinfo:read") > -1 && permissions.indexOf("tenant-journey:read") > -1) {
      redirect.push("/people/tenants/details/" + row.tenant_id);
    } else {
      setTenantModalOpen(true);
      setTenantDetails(row?.tenant_details);
    }
  }

  const sortableHead = (label, key, width) => (<TableCell className={`${styles.TableHead}`} style={{ width }} onClick={() => changeSortOrder(key)}>
    {label}
    <SortIcon active={sortItem === key} order={sortOrder} />
  </TableCell>);

  if (contentLoading) {
    return (<div className="Loader TableLoader">
      <TableLoader />
    </div>);
  }

  return (
    <div className={refetching ? `${styles.IncomeListUnderSec} ${styles.Refetching}` : `${styles.IncomeListUnderSec}`}>

      <MobileViewList
        rowsData={rowsData}
        actions={actions}
        setTenantModalOpen={setTenantModalOpen}
        setTenantDetails={setTenantDetails}
      />

      <TableContainer className={`${styles.TableDiv} CustomTableDiv`}>
        <Table>
          <TableHead>
            <TableRow>
              {sortableHead("TENANT / GUEST", "tenant_name", 260)}
              {sortableHead("LICENSE", "license", 150)}
              {sortableHead("VEHICLE", "make", 200)}
              {sortableHead("PROPERTY / UNIT", "property_name", 200)}
              {sortableHead("APPROVAL", "submitted_at", 190)}
              {/* <TableCell className={`${styles.TableHead}`} style={{ width: 170 }}>PARKING PASS</TableCell> */}
              <TableCell className={`${styles.TableHead}`} style={{ width: 36 }} />
              <TableCell className={`${styles.TableHead}`} style={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsData.map((row, index) => {
              const rowClass = index % 2 === 0 ? styles.MainRowOdd : styles.MainRowEven;
              const isExpanded = expandedRowKey === row.key;
              const isGuest = row.is_guest === 1;
              const isVehicle = isVehicleRow(row);
              const isRejected = isRejectedRequest(row);
              const thumb = rowThumb(row);
              const fullImage = rowPhotos(row)[0]?.url || thumb;
              // const showMenu = canUpdate && (isVehicle || !isGuest || isRejected);
              const showMenu = canUpdate && (isVehicle || isRejected);

              return (
                <Fragment key={row.key}>
                  <TableRow
                    className={isExpanded ? `${rowClass} ${styles.RowExpanded}` : rowClass}
                    onClick={() => toggleRow(row.key)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell className={`${styles.TableBodyText} ${styles.ProfileImg} ${!isGuest ? styles.Hover : ''}`}>
                      {isGuest ? (
                        <div className={styles.TenantInside}>
                          <div className={styles.AvatarCircle} style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                            {row.initials}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <span className={styles.GuestNameText}>{row.name}</span>
                              <span className={styles.GuestBadge}>Guest</span>
                              {row.isNew && <p className={styles.NewBadgeSmall}>New</p>}
                            </div>
                            <span className={styles.GuestPhoneText}>{row.tenant_name ? `guest of ${row.tenant_name}` : row.phone}</span>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.TenantInside}>
                          {row?.tenant_image ? <img src={row?.tenant_image} alt="" onMouseEnter={(e) => { setTenantImage(row?.tenant_image2 || row?.tenant_image); handlePopoverOpen2(e); }} onMouseLeave={handlePopoverClose2} /> : <div className={styles.AvatarCircle} style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                            {row.initials}
                          </div>}
                          <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                            <span className={styles.TenantNameText} onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToTenantetails(row); }}>{row.name}</span>
                            {row.isNew && <p className={styles.NewBadgeSmall}>New</p>}
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={styles.TableBodyText}>
                      <div className={`${styles.DisplayFlex} ${styles.LicenseSec}`}>
                        <span>{row.license}</span>
                        {thumb ? <div className={`${styles.CarSec}`} onMouseEnter={(e) => { setCarImage(fullImage); handlePopoverOpen(e); }} onMouseLeave={handlePopoverClose}>
                          <ListThumb thumb={thumb} full={fullImage} />
                        </div> :
                          <div className={`${styles.NoCarSec}`}>
                            <img src={"/static/img/no-car.png"} alt="" />
                          </div>}
                      </div>
                    </TableCell>
                    <TableCell className={styles.TableBodyText}>
                      <div className={styles.VehicleCell}>
                        <span className={styles.VehicleMakeModel}>{row.make} {row.model}</span>
                        {row.color && <span className={styles.VehicleColorDot}></span>}
                        <span className={styles.VehicleColor}>{row.color}</span>
                      </div>
                    </TableCell>
                    <TableCell className={`${styles.TableBodyText} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (row?.property_id) redirect.push("/properties/details/" + row.property_id); }}>
                      <div className={styles.PropertyUnitCell}>
                        <span className={styles.PropertyText}>{row.property}</span>
                        <p className={styles.UnitText}>{row.unit}</p>
                      </div>
                    </TableCell>
                    <TableCell className={styles.TableBodyText}>
                      <ApprovalBadge row={row} />
                    </TableCell>
                    {/* Parking pass column: payments / passes are no longer used for guest access
                    <TableCell className={styles.TableBodyText}>
                      <PassCell row={row} />
                    </TableCell> */}
                    <TableCell className={`${styles.TableBodyText} ${styles.ChevronCell}`}>
                      <ChevronRight size={18} className={isExpanded ? `${styles.RowChevron} ${styles.RowChevronOpen}` : styles.RowChevron} />
                    </TableCell>
                    <TableCell className={`${styles.TableBodyText} ${styles.KebabCell}`}>
                      {showMenu && (
                        <Dropdown className={`${styles.ActionLeaseBUSecStatic}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                          <Dropdown.Toggle className={`${styles.ActionsLeaseBU} ActionsBU`} id={`vehicle-actions-${row.key}`}>
                            <MoreVertical />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className={`${styles.ActionsBUList} ActionsBUList`}>
                            {(isVehicle || isRejected) && (<Dropdown.Item className={`${styles.EditActionsBUListItem}`} onClick={() => (isRejected ? actions?.openEditRequest(row) : actions?.openEdit(row))}>
                              <Edit /> Edit
                            </Dropdown.Item>)}
                            {/* Send update link is no longer used
                            {!isGuest && (<Dropdown.Item className={`${styles.EditActionsBUListItem}`} onClick={() => actions?.openSendLink(row)}>
                              <Send /> Send update link
                            </Dropdown.Item>)} */}
                            {canSendUnlockLink(row) && (<Dropdown.Item className={`${styles.EditActionsBUListItem}`} onClick={() => actions?.sendAccessLink(row)}>
                              <Send /> Send unlock link (SMS)
                            </Dropdown.Item>)}
                            {(isVehicle || (isRejected && actions?.canDelete)) && (<Dropdown.Item className={`${styles.EditActionsBUListItem} ${styles.Delete}`} onClick={() => (isRejected ? actions?.openDeleteRequest(row) : actions?.openDelete(row))}>
                              <Trash2 /> Delete
                            </Dropdown.Item>)}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className={styles.ExpandedRow}>
                      <TableCell colSpan={COLUMN_COUNT} className={`${styles.ExpandedCell} ${styles.DetailCell}`}>
                        <VehicleDetailPanel
                          row={row}
                          canUpdate={canUpdate}
                          onChanged={() => { setExpandedRowKey(null); if (onListChange) onListChange(); }}
                          onEdit={actions?.openEdit}
                          onDelete={actions?.openDelete}
                          onEditRequest={actions?.openEditRequest}
                          onDeleteRequest={actions?.canDelete ? actions?.openDeleteRequest : undefined}
                          notifySuccess={actions?.notifySuccess}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sentinel: entering the viewport loads the next chunk (table on desktop, cards on mobile) */}
      <div ref={sentinelRef} className={`${styles.ScrollSentinel}`} />

      {loadingMore && (<div className={`${styles.ScrollStatus}`}>
        <p>Loading more vehicles...</p>
      </div>)}

      {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
        <p>Couldn't load more vehicles.</p>
        <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchVehicles(offset, false)}>Retry</button>
      </div>)}

      {loadError && (<div className={`${styles.NoDataMain}`}>
        <p className={`${styles.NoDataText}`}>Something went wrong!</p>
        <p className={`${styles.NoDataTextSub}`}>we couldn't load the vehicles.</p>
        <button type="button" className={`${styles.RetryBu}`} onClick={() => { loadedKeyRef.current = requestKey; fetchVehicles(0, true); }}>Retry</button>
      </div>)}

      {!loadError && rowsData.length === 0 && (<div className={`${styles.NoDataMain}`}>
        <div className={`${styles.NoDataIMG}`}>
          <img src="/static/img/no-car.png" alt="No Data" />
        </div>
        <p className={`${styles.NoDataText}`}>Unfortunately!</p>
        <p className={`${styles.NoDataTextSub}`}>
          we couldn't find any vehicle at this time.
        </p>
      </div>)}

      <Popover
        id="mouse-over-popover"
        sx={{ pointerEvents: "none" }}
        open={open}
        anchorEl={anchorEl}
        action={carPopoverActionRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div className={`${styles.PopupImgSec}`}>
          <img
            src={carImage}
            alt=""
            onLoad={() => carPopoverActionRef.current?.updatePosition()}
          />
        </div>
      </Popover>

      <Popover
        id="mouse-over-popover2"
        sx={{ pointerEvents: "none" }}
        open={open2}
        anchorEl={anchorEl2}
        action={tenantPopoverActionRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handlePopoverClose2}
        disableRestoreFocus
      >
        <div className={`${styles.PopupImgSec}`}>
          <img
            src={tenantImage}
            alt=""
            style={{ maxWidth: '400px', maxHeight: '600px' }}
            onLoad={() => tenantPopoverActionRef.current?.updatePosition()}
          />
        </div>
      </Popover>

    </div>
  );
}

export default VehicleList;